import { 
  Heart, Home, Pill, ShoppingBag, Calendar, 
  FileText, Eye, Users, Video, UserPlus,
  MessageCircle, TrendingUp
} from 'lucide-react';
import EnhancedSEO from '@/components/seo/EnhancedSEO';
import { OG_IMAGES } from '@/constants/seoImages';
import HeroSectionB from '@/components/older-adults-b/HeroSectionB';
import WhatIsThriveMaxSection from '@/components/older-adults-b/WhatIsThriveMaxSection';
import HowItWorksSectionB from '@/components/older-adults-b/HowItWorksSectionB';
import FeatureBlocksSection from '@/components/older-adults-b/FeatureBlocksSection';
import PrivacySectionB from '@/components/older-adults-b/PrivacySectionB';
import CTASectionB from '@/components/older-adults-b/CTASectionB';
import TestimonialsSection from '@/components/older-adults/TestimonialsSection';
import BridgeToFeaturesSection from '@/components/older-adults-b/BridgeToFeaturesSection';

const OlderAdults = () => {
  const testimonials = [
    {
      name: "Margaret S., 72",
      content: "The vitals tracker helped me catch high blood pressure early, and the medication reminders ensure I never miss a dose. My doctor is impressed with how organized my health records are now!"
    },
    {
      name: "Robert T., 68", 
      content: "I love the brain health games and photo gallery features. Sharing memories with my grandchildren through the platform brings such joy, and the cognitive exercises keep me sharp."
    },
    {
      name: "Dorothy L., 75",
      content: "The food delivery support and recipes hub have been lifesavers! I can order healthy meals easily and try new recipes. The pet care tools help me take better care of my cat Whiskers too."
    }
  ];

  const coreFeatures = [
    { icon: Home, title: "Home Safety Checks", description: "Room-by-room hazard detection from a single photo." },
    { icon: Heart, title: "Wellness & Vitals", description: "Blood pressure, weight, sleep, mood, all in one place." },
    { icon: Pill, title: "Medication Management", description: "Smart reminders + FDA database for your medications." },
    { icon: TrendingUp, title: "Personal ThriveScore", description: "One number showing your overall wellness and progress." },
    { icon: MessageCircle, title: "7 AI Companions", description: "Friendly guidance on health, safety, cooking, and more." },
    { icon: Eye, title: "Assessments", description: "Vision, driving, mobility, and daily living self-checks." },
  ];

  const lifeEnrichment = [
    { icon: ShoppingBag, title: "Recipes & Food Delivery" },
    { icon: FileText, title: "Books & Reading" },
    { icon: Calendar, title: "Art Museums & Galleries" },
    { icon: Users, title: "Your ThriveCircle" },
    { icon: Video, title: "Video Calls" },
    { icon: UserPlus, title: "Hobbies & My Garden" },
  ];

  return (
    <>
      <EnhancedSEO 
        title="Essential for Older Adults"
        description="Essential gives you room-by-room safety insights, daily check-ins, and wellness tracking to stay independent at home longer. See how it works."
        keywords="Essential, older adults, aging in place, senior independence, home safety, wellness tracking, senior community, thrive at home"
        canonical="https://seniorthrive.com/older-adults"
        image={OG_IMAGES.olderAdults}
        breadcrumbs={[
          { name: "Home", url: "https://seniorthrive.com/" },
          { name: "Essential for Older Adults", url: "https://seniorthrive.com/older-adults" }
        ]}
        schema={{
          type: "Service",
          name: "Essential for Older Adults",
          description: "Essential: Complete wellness and safety platform for independent living at home",
          provider: "SeniorThrive"
        }}
      />
      
      <HeroSectionB />
      
      <div className="px-4">
        <div className="container mx-auto max-w-6xl">
          
          <WhatIsThriveMaxSection />
          
          <HowItWorksSectionB />
          
          <FeatureBlocksSection />
          
          {/* Core Features */}
          <section className="py-16 lg:py-20">
            <div className="text-center mb-12">
              <h2 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
                The Tools That Keep You Independent
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Safety, wellness, and support, designed to work together, not as separate apps.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {coreFeatures.map((feature, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 p-6 rounded-md bg-background/50 shadow-raised hover:-translate-y-0.5 transition-all"
                >
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground mb-1">{feature.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Life enrichment */}
            <div className="bg-gradient-to-br from-accent/10 to-secondary/15 rounded-lg p-8 lg:p-10">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-foreground mb-3">
                  Plus Everything That Makes Home Life Enjoyable
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Thriving is not just about safety, it is about living fully. Recipes, hobbies,
                  community, culture, and connection are all part of SeniorThrive.
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {lifeEnrichment.map((item, i) => (
                  <div key={i} className="flex flex-col items-center gap-2 p-4 rounded-md bg-card/80 shadow-raised">
                    <item.icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                    <span className="text-foreground text-center leading-snug">{item.label || item.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Is This Right for Me? */}
          <section className="py-16 lg:py-20">
            <div className="text-center mb-12">
              <h2 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
                "Am I Ready for This?"
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                If you live at home and want to keep it that way, yes. SeniorThrive grows
                with you, no matter where you are in your journey.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto mb-10">
              {[
                {
                  age: 'In your 50s or 60s',
                  message: 'Start building healthy habits and a safe home environment early. The sooner you begin, the stronger your foundation.',
                },
                {
                  age: 'In your 70s or 80s',
                  message: 'Stay in control of your wellness and bring a family member in when it feels right. You decide the pace.',
                },
                {
                  age: '90 and beyond',
                  message: 'We have had people in their 90s discover new ways to stay connected and confident. It is never too late to thrive.',
                },
              ].map((stage, i) => (
                <div key={i} className="text-center p-6 rounded-md bg-card shadow-raised">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-md flex items-center justify-center mx-auto mb-4 font-bold text-lg shadow-floating">
                    {i + 1}
                  </div>
                  <p className="text-primary font-bold mb-2">{stage.age}</p>
                  <p className="text-muted-foreground leading-relaxed">{stage.message}</p>
                </div>
              ))}
            </div>

            <p className="text-center text-lg text-muted-foreground max-w-2xl mx-auto">
              There is no wrong time to start, only your time. And if your needs change,
              SeniorThrive changes with you.
            </p>
          </section>
          
          <PrivacySectionB />
          
          <TestimonialsSection testimonials={testimonials} />
          
          <BridgeToFeaturesSection />
          
          <CTASectionB />
          
          {/* Bottom spacing */}
          <div className="h-8" />
        </div>
      </div>
    </>
  );
};

export default OlderAdults;
